/*
  This is the core code for the search feature.  
*/

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useApolloClient } from "@apollo/react-hooks";
import {
  advancedCharacteristics,
  advancedCharacteristics2,
  bpm,
  characteristics,
  duration,
  keys
} from "../../utils/searchFilters";

import {
  backendValue,
  convertFiltersToFrontend,
  convertToFrontend,
  relaxEqualRangeValues
} from "../../utils/rangeConversion";
import {
  filterDuplicate,
  filterIsDefault,
  formatResponse,
  generateDefaultRanges,
  isItemLengthOrBpm,
  removeDefault
} from "../../utils/searchHelpers";
import { getEffectiveSort } from "../../utils/sortHelpers";
import { dedupeById } from "../../utils/helpers";
import { AuthContext } from "../AuthProvider";

import {
  DELETE_PLAYLIST_TRACK,
  FETCH_SIMILAR_TRACKS,
  GET_ALBUM,
  GET_AUTOCOMPLETE,
  GET_PLAYLIST,
  GET_TRACK,
  SEARCH,
  SUGGEST_QUERY,
  UPDATE_PLAYLIST_TRACK_ORDER
} from "./queries";
import { runSimilarityUpload } from "./runSimilarityUpload";
import { withRouter } from "react-router-dom";
import {
  ENTITY_URL_PARAM_IDS,
  ENTITY_FILTER_NAMES
} from "../../constants/entityTypes";
import {
  NATURAL_LANGUAGE_FILTER_NAME,
  SEARCH_MODES,
  TRACK_VIEW
} from "../../components/SearchInput/constants";

export const SearchContext = React.createContext();

const PAGINATION_SIZE = 50;
const TEXT_QUERY_FILTER_NAME = "selectable";

const formatAllTracks = array => {
  return array.map(item => ({
    ...item._source
  }));
};

const sortModes = ["max", "sum", "avg"];

function randomIntFromInterval(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

// seed is used to filter out duplicate tracks. (logic is done in backend)
localStorage.setItem("seed", Math.floor(Math.random() * 1000000));
let seed = parseInt(localStorage.getItem("seed"));

// seed used to have some randomization on sort options
localStorage.setItem("randomSortMode", sortModes[randomIntFromInterval(0, 2)]);
let randomSortMode = localStorage.getItem("randomSortMode");

const changeSearchSeeds = () => {
  localStorage.setItem("seed", Math.floor(Math.random() * 1000000));
  localStorage.setItem(
    "randomSortMode",
    sortModes[randomIntFromInterval(0, 2)]
  );
  seed = parseInt(localStorage.getItem("seed"));
  randomSortMode = localStorage.getItem("randomSortMode");
};

const SearchProvider = ({ children, location }) => {
  // for graphql requests
  const client = useApolloClient();

  // array of filters applied
  const [searchFilters, setFilters] = useState([]);

  // array of filters applied converted to frontend
  const [frontendSearchFilters, setFrontendFilters] = useState([]);

  // holds filters that is displayed when hovered on
  const [hoveredFilter, setHoveredFilter] = useState(null);

  const [selectedAggregate] = useState({});

  // search term, text input
  const [searchValue, setSearchValue] = useState("");

  // track currently uploaded for similarity search
  const [uploadedTrack, setUploadedTrack] = useState({
    status: "idle",
    filename: null
  });

  // Search bar mode (classic "search" vs "similarity"). Lifted here so that the
  // browse-view track dropdowns (SingleTrackView, per-row TrackDropdown) can
  // default to showing similar tracks instead of variations while in similarity
  // mode. `defaultTrackView` is the derived default each dropdown falls back to.
  const [searchMode, setSearchMode] = useState(SEARCH_MODES.SEARCH);
  const defaultTrackView =
    searchMode === SEARCH_MODES.SIMILARITY
      ? TRACK_VIEW.SIMILAR
      : TRACK_VIEW.VARIATION;

  // Drives the real similar-tracks-by-upload flow whenever a file enters the
  // "analyzing" state
  // - on success, inject the returned tracks straight into the results grid
  // - on failure, surface a "failed" status for the UI.
  //
  // Note: we intentionally do NOT flip the grid into a loading state here.
  // Cyanite analysis can take a while, so we keep the existing browse results
  // visible and playable and only swap them out once similar tracks are ready
  useEffect(() => {
    if (uploadedTrack.status !== "analyzing" || !uploadedTrack.file)
      return undefined;
    let cancelled = false;
    (async () => {
      const result = await runSimilarityUpload(client, uploadedTrack.file);
      if (cancelled) return;
      if (result.status === "ready") {
        const similar = result.similarTracks || [];
        // show upload results as main browse
        wipeData();
        setExactTracks(similar);
        setExactTracksPaginator(false);
        setSuggestedTracks([]);
        setSuggestedCursor(null);
        setIsSearching(true);
        setLoadingSuggestions(true);

        // fill suggested tracks w filtered-similarity page seeded from the best match, falling back to random
        const topId = similar[0]?.id;
        let primed = [];
        let primedFromSimilar = false;
        if (topId) {
          try {
            const { data } = await client.query({
              query: FETCH_SIMILAR_TRACKS,
              fetchPolicy: "network-only",
              variables: { id: topId }
            });
            primed = filterDuplicate(similar, data?.similarTracks || []);
            primedFromSimilar = primed.length > 0;
          } catch (err) {
            console.error("Search provider: upload similar suggestions ", err);
          }
        }
        if (!primed.length) {
          const random = await fetchFallbackSuggestions(0);
          primed = filterDuplicate(similar, random);
        }
        if (cancelled) return;
        setSuggestedTracks(dedupeById(primed));
        setSuggestedCursor(
          primedFromSimilar
            ? { phase: "fallback", from: 0 }
            : { phase: "fallback", from: PAGINATION_SIZE }
        );
        setLoadingSuggestions(false);

        setUploadedTrack(prev => ({
          ...prev,
          status: "analyzed",
          resultCount: similar.length
        }));
      } else {
        setUploadedTrack(prev => ({
          ...prev,
          status: "failed",
          error: result.error
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedTrack.status, uploadedTrack.file, client]);

  // array of autocomplete suggestions
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState([]);

  // filters to be displayed in the sidebar;

  const all = [
    ...characteristics,
    ...advancedCharacteristics,
    ...advancedCharacteristics2
  ];
  const [allCharacteristics, setChars] = useState(all);

  // Mobile filter drawer visibility. Lives here (rather than in Browse) so the
  // global Header can open it while the Browse page renders the drawer itself.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const openFilterDrawer = useCallback(() => setFilterDrawerOpen(true), []);
  const closeFilterDrawer = useCallback(() => setFilterDrawerOpen(false), []);

  // this is to display the tag for album and playlist on the header.
  const [selectedTrackList, setSelectedTrackList] = useState(undefined);
  const [allTags, setAllTags] = useState([]);
  const [allGenres, setAllGenres] = useState([]);
  const [aggregates, setAllAggregates] = useState([]);
  const [allInstruments, setAllInstruments] = useState([]);
  const [allKeys] = useState(keys);

  const [allTracks, setAllTracks] = useState([]);

  // id token for the most recent track-list request
  const activeTrackRequestRef = useRef(null);

  // Identity of whichever view `allTracks` currently belongs to: an entity id
  // for an open album/playlist, or null for the plain browse list
  const allTracksContextRef = useRef(null);

  // live mirror of selectedTrackList for reading inside async callbacks whose
  // captured closure would otherwise hold a stale value
  const selectedTrackListRef = useRef(undefined);
  selectedTrackListRef.current = selectedTrackList;

  // Params of the most recent unified search, so fetchMoreExactTracks can page it by offset.
  const currentSearchRef = useRef(null);

  // Browse filters parked while an entity is open
  const browseFilterSnapshotRef = useRef(null);
  // Previous entity id, to classify enter/switch/leave transitions
  const prevEntityIdRef = useRef(null);
  // The sort/search-change effect must NOT fetch on initial mount, prevents double fetch
  const sortDidMountRef = useRef(false);
  const searchEffectDidMountRef = useRef(false);

  // Lightweight entity shape from the autocomplete payload so already loaded info can populate
  // the header widget faster
  const [pendingWidgetEntity, setPendingWidgetEntity] = useState(null);

  const [exactTracks, setExactTracks] = useState([]);
  const [suggestedTracks, setSuggestedTracks] = useState([]);
  const [filteredSuggestedTracks, setFilteredSuggestedTracks] = useState([]);
  const [isNlFallback, setIsNlFallback] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // data from frontend search tags & instruments
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [genreSearchResults, setGenreSearchResults] = useState([]);
  const [instrumentSearchResults, setInstrumentSearchResult] = useState([]);
  const [aggregateResults, setAggregateResults] = useState([]);

  // TODO: refactor to tracks context
  const [trackToBeDownloaded, setDownloadTrackState] = useState({});

  const [isLoadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [allTracksPaginator, setAllPaginator] = useState(true);
  const [suggestPaginator, setSuggestPaginator] = useState(undefined);
  const [exactTracksPaginator, setExactTracksPaginator] = useState(undefined);
  const [suggestedCursor, setSuggestedCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingMoreExact, setLoadingMoreExact] = useState(false);
  const [textSearching, setTextSearching] = useState(false);
  const [selectedAggregates, setAggregates] = useState([]);

  const [singleTrack, setSingleTrack] = useState({});
  const [tracksCache, setTrackCache] = useState({});

  const [fetchingDownloadTrack, setFetchingDownloadTrack] = useState(false);

  const [sortType, setSortType] = useState("none");

  const [showAllFiltersByDefault, setShowAllFiltersByDefault] = useState(
    () => localStorage.getItem("showAllFiltersByDefault") === "true"
  );
  const [
    showVariationsInSearchResults,
    setShowVariationsInSearchResults
  ] = useState(
    () => localStorage.getItem("showVariationsInSearchResults") === "true"
  );
  const [showOnlyTracksWithStems, setShowOnlyTracksWithStems] = useState(
    () => localStorage.getItem("showOnlyTracksWithStems") === "true"
  );

  const toggleShowAllFiltersByDefault = checked => {
    setShowAllFiltersByDefault(checked);
    localStorage.setItem("showAllFiltersByDefault", checked);
  };

  const toggleShowVariationsInSearchResults = checked => {
    setShowVariationsInSearchResults(checked);
    localStorage.setItem("showVariationsInSearchResults", checked);
  };

  const toggleShowOnlyTracksWithStems = checked => {
    setShowOnlyTracksWithStems(checked);
    localStorage.setItem("showOnlyTracksWithStems", checked);
  };

  const allSearchResults = [
    ...allTracks,
    ...autoCompleteSuggestions,
    ...allTags,
    ...allGenres,
    ...aggregateResults,
    ...instrumentSearchResults
  ];

  // Active within-entity filters
  const withinEntityFilters = useMemo(
    () =>
      relaxEqualRangeValues(
        searchFilters.filter(f => !ENTITY_FILTER_NAMES.includes(f.filterName))
      ),
    [searchFilters]
  );

  const searchFiltersRef = useRef(searchFilters);
  searchFiltersRef.current = searchFilters;
  const selectedAggregatesRef = useRef(selectedAggregates);
  selectedAggregatesRef.current = selectedAggregates;

  const auth = useContext(AuthContext);

  const isAdmin = !!auth?.user?.adminUser;

  /*
    receive tags, instruments and aggregates from auth context.
  */
  useEffect(() => {
    const { aggregates, allTags, allInstruments, allGenres } = auth;
    setAllAggregates(aggregates);
    setAllTags(allTags);
    setAllInstruments(allInstruments);
    setAllGenres(allGenres);
  }, [auth]);

  /*
  convert applied filters to frontend.
  */
  useEffect(() => {
    const convertedValues = convertFiltersToFrontend(searchFilters);
    setFrontendFilters(convertedValues);
  }, [searchFilters]);

  /*
    fetch unfiltered tracks.
  */
  const fetchTracks = useCallback(async () => {
    const requestToken = {};
    activeTrackRequestRef.current = requestToken;
    // allTracks is about to hold the plain browse list (no entity)
    allTracksContextRef.current = null;
    setAllTracks([]);
    let variables = {
      filters: [],
      size: PAGINATION_SIZE,
      seed,
      randomSortMode: randomSortMode,
      from: 0,
      sort: getEffectiveSort(sortType, false)
    };

    try {
      setLoadingTracks(true);
      const res = await client.query({
        query: SUGGEST_QUERY,
        fetchPolicy: "network-only",
        variables
      });

      // A newer request (e.g. an Album/Playlist selection) superseded this
      // browse fetch while it was in flight, so don't apply stale data over the current view
      if (activeTrackRequestRef.current !== requestToken) return;

      let tracks = [];

      tracks = res.data.suggest.map(item => item._source);
      if (tracks < PAGINATION_SIZE) setAllPaginator(false);

      setAllTracks(tracks);
      setLoadingTracks(false);
    } catch (err) {
      console.error("fetch tracks ", err);
    }
    // eslint-disable-next-line
  }, [client, sortType]);

  /*
    this object contains pagination related properties for easy access for
    "true" for search/filtering pagination and
    "false" for unfiltered track pagination
   */
  const pagination = {
    paginator: {
      true: suggestPaginator,
      false: allTracks.length + 1
    },
    tracks: {
      true: suggestedTracks,
      false: allTracks
    },
    setPaginator: {
      true: setSuggestPaginator,
      false: setAllPaginator
    },
    setTracks: {
      true: setSuggestedTracks,
      false: setAllTracks
    },
    filters: {
      true: relaxEqualRangeValues(searchFilters),
      false: []
    },
    formatter: {
      true: formatResponse,
      false: formatAllTracks
    },
    seed: {
      true: seed,
      false: seed
    },
    randomSortMode: {
      true: randomSortMode
    }
  };

  const fetchMoreTracks = async () => {
    // using isSearching boolean to distinguish between
    // browse tracks and searched tracks
    // object mapping fields is above this function
    // if searching
    if (isSearching && !pagination.paginator[isSearching]) return;

    // is not searching and paginator is set to false
    if (!isSearching && !allTracksPaginator) return;

    // Entity views (Album/Playlist) display a complete, fixed track list, no infinite scroll
    if (!isSearching && selectedTrackList) {
      setLoadingMore(false);
      return;
    }

    // don't paginate until first page has loaded
    if (!isSearching && allTracks.length === 0) {
      setLoadingMore(false);
      return;
    }

    setLoadingMore(true);

    // Capture the active request (i.e. click to album/playlist)
    const requestToken = activeTrackRequestRef.current;

    try {
      const res = await client.query({
        query: SUGGEST_QUERY,
        fetchPolicy: "network-only",
        variables: {
          filters: pagination.filters[isSearching] || [],
          from: pagination.paginator[isSearching],
          randomSortMode: randomSortMode,
          size: PAGINATION_SIZE,
          seed: pagination.seed[isSearching],
          sort: getEffectiveSort(sortType, isSearching)
        }
      });

      // An entity selection or fresh browse fetch replaced the list while this
      // page was loading, appending now would clobber it with stale tracks
      if (activeTrackRequestRef.current !== requestToken) {
        return setLoadingMore(false);
      }

      // read live ref to make sure we're not appending stale data
      if (!isSearching && selectedTrackListRef.current) {
        return setLoadingMore(false);
      }

      const tracks = pagination.formatter[isSearching](res.data.suggest);
      const added = dedupeById([...pagination.tracks[isSearching], ...tracks]);

      if (
        // if we have after key and it's the same as the previous
        res.data.suggest.after_key &&
        res.data.suggest.after_key.parentTrackId ===
          pagination.paginator[isSearching].parentTrackId
      ) {
        return setLoadingMore(false);
      }

      if (!isSearching && tracks.length < PAGINATION_SIZE)
        setAllPaginator(false);

      if (isSearching) {
        pagination.setPaginator[isSearching](res.data.suggest.after_key);
        let exactTracksHash = {};
        exactTracks.forEach(item => (exactTracksHash[item.id] = item.title));
        const filteredSuggestions = added.filter(
          item => !exactTracksHash[item.id]
        );

        pagination.setTracks[isSearching](dedupeById(filteredSuggestions));
        setLoadingMore(false);
        return;
      }

      pagination.setTracks[isSearching](dedupeById(added));
      setLoadingMore(false);
    } catch (err) {
      setLoadingMore(false);
      console.error("search provider fetch more", err);
    }
  };

  const fetchMoreExactTracks = async () => {
    // exactTracksPaginator holds the next offset, or false when exhausted
    if (!exactTracksPaginator) {
      setLoadingMoreExact(false);
      return;
    }
    const active = currentSearchRef.current;
    if (!active) {
      setLoadingMoreExact(false);
      return;
    }
    setLoadingMoreExact(true);
    const from = exactTracksPaginator;

    try {
      const { data } = await client.query({
        query: SEARCH,
        fetchPolicy: "network-only",
        variables: {
          q: active.queryText,
          mode: active.mode,
          seedTrackId: active.seedTrackId,
          filters: active.filters,
          contextId: active.contextId,
          contextType: active.contextType,
          from,
          size: PAGINATION_SIZE,
          sort: active.sort,
          seed: active.seed
        }
      });
      const result = data.search || {};
      const more = result.exact || [];

      setExactTracks(prev => dedupeById([...prev, ...more]));
      setExactTracksPaginator(
        result.exactHasMore ? from + PAGINATION_SIZE : false
      );
      setLoadingMoreExact(false);
    } catch (err) {
      console.error("Search provider: fetchMoreExactTrack ", err);
      setLoadingMoreExact(false);
    }
  };

  /*
    Pages the related-results stream so scroll never dead-ends while searching
  */
  const fetchMoreSuggested = async () => {
    const cursor = suggestedCursor;
    if (!cursor) {
      setLoadingMore(false);
      return;
    }
    setLoadingMore(true);

    try {
      if (cursor.phase === "filtered") {
        const active = currentSearchRef.current;
        if (!active) {
          setLoadingMore(false);
          return;
        }
        const { data } = await client.query({
          query: SEARCH,
          fetchPolicy: "network-only",
          variables: {
            q: active.queryText,
            mode: active.mode,
            seedTrackId: active.seedTrackId,
            filters: active.filters,
            contextId: active.contextId,
            contextType: active.contextType,
            from: cursor.from,
            size: PAGINATION_SIZE,
            sort: active.sort,
            seed: active.seed
          }
        });
        const result = data.search || {};
        const more = result.suggested || [];
        setSuggestedTracks(prev => dedupeById([...prev, ...more]));
        setSuggestedCursor(
          result.suggestedHasMore
            ? { phase: "filtered", from: cursor.from + PAGINATION_SIZE }
            : { phase: "fallback", from: 0 }
        );
      } else {
        const fetched = await fetchFallbackSuggestions(cursor.from);
        const more = filterDuplicate(exactTracks, fetched);
        setSuggestedTracks(prev => dedupeById([...prev, ...more]));
        // Advance by the raw page size; a short page means the library is spent.
        setSuggestedCursor(
          fetched.length >= PAGINATION_SIZE
            ? { phase: "fallback", from: cursor.from + PAGINATION_SIZE }
            : null
        );
      }
      setLoadingMore(false);
    } catch (err) {
      console.error("Search provider: fetchMoreSuggested ", err);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasEntityParam = ENTITY_URL_PARAM_IDS.some(p => params.has(p));
    if (hasEntityParam) {
      // Show skeletons until query resolves instead of fetching (and flashing) random tracks
      setLoadingTracks(true);
      return;
    }
    fetchTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // filter out duplicate tracks from suggest
    const filteredSuggestions = filterDuplicate(exactTracks, suggestedTracks);
    setFilteredSuggestedTracks(filteredSuggestions);
  }, [suggestedTracks, exactTracks]);

  /**
   * main effect, fired when the search filters change
   */
  useEffect(() => {
    // While a similarity upload owns the grid, don't let a filter-driven search
    // clobber its results
    if (
      uploadedTrack.status === "analyzing" ||
      uploadedTrack.status === "analyzed"
    ) {
      return;
    }

    // Keep the seed stable on the initial mount so the first browse fetch and
    // its pagination share one ordering
    if (searchEffectDidMountRef.current) {
      changeSearchSeeds();
    } else {
      searchEffectDidMountRef.current = true;
    }

    // Similar-track entity context: the selected Track seeds a SIMILAR search
    // TEMP CYANITE ADMIN GATE
    if (
      isAdmin &&
      selectedTrackList?.type === "Track" &&
      searchMode === SEARCH_MODES.SIMILARITY
    ) {
      setIsSearching(true);
      const withinFilters = relaxEqualRangeValues(
        searchFilters.filter(
          item => !ENTITY_FILTER_NAMES.includes(item.filterName)
        )
      );
      executeSearch("SIMILAR", "", withinFilters);
      return;
    }

    // If there's an active NL query, we run the Cyanite search constrained by any
    // other active filters and re-run it whenever those filters change so the results
    // stay in sync w filters
    const nlPill = searchFilters.find(
      item => item.filterName === NATURAL_LANGUAGE_FILTER_NAME
    );
    if (nlPill) {
      const backendFilters = relaxEqualRangeValues(
        searchFilters.filter(
          item => item.filterName !== NATURAL_LANGUAGE_FILTER_NAME
        )
      );
      setIsSearching(true);
      executeSearch("NATURAL_LANGUAGE", nlPill.filterValue, backendFilters);
      return;
    }

    if (
      searchFilters.length === 1 &&
      ENTITY_FILTER_NAMES.includes(searchFilters[0].filterName)
    ) {
      setIsSearching(false);
      return;
    }

    // While an Album/Playlist is selected, its track list (loaded via
    // queryAlbumDetail/queryPlaylistDetail) is the source of truth
    if (
      selectedTrackList &&
      ["Album", "Playlist"].includes(selectedTrackList.type)
    ) {
      setIsSearching(false);
      return;
    }

    if (searchFilters.length === 0) {
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    const textPill = searchFilters.find(
      item => item.filterName === TEXT_QUERY_FILTER_NAME
    );
    const mode = textPill ? "TEXT" : "FILTER_ONLY";
    const backendFilters = relaxEqualRangeValues(
      searchFilters.filter(item => item.filterName !== TEXT_QUERY_FILTER_NAME)
    );
    executeSearch(mode, textPill ? textPill.filterValue : "", backendFilters);
    // eslint-disable-next-line
  }, [searchFilters, client, fetchTracks, searchMode, uploadedTrack.status]);

  /*
    Entity filter-context controller
    - none -> entity: snapshot browse filters, clear the active slot
    - entity -> entity: wipe entity filters (keep snapshot)
    - entity -> none: restore the browse snapshot
  */
  useEffect(() => {
    const currentId = selectedTrackList?.entity?.id ?? null;
    const prevId = prevEntityIdRef.current;
    // Same entity id: an in-place mutation (e.g. reorder, track removal) — no
    // entity transition to handle
    if (currentId === prevId) return;

    const keepOnlyEntityChips = prev =>
      prev.filter(f => ENTITY_FILTER_NAMES.includes(f.filterName));

    if (!prevId && currentId) {
      // ENTER: park browse (non-entity) filters, clear the active slot
      browseFilterSnapshotRef.current = {
        searchFilters: searchFiltersRef.current.filter(
          f => !ENTITY_FILTER_NAMES.includes(f.filterName)
        ),
        selectedAggregates: selectedAggregatesRef.current
      };
      setFilters(keepOnlyEntityChips);
      setAggregates([]);
      applySidebarFromFilters([]);
    } else if (prevId && currentId) {
      // SWITCH: wipe entity filters, keep the snapshot
      setFilters(keepOnlyEntityChips);
      setAggregates([]);
      applySidebarFromFilters([]);
    } else if (prevId && !currentId) {
      // LEAVE: restore the browse snapshot
      const snap = browseFilterSnapshotRef.current;
      if (snap) {
        setFilters(snap.searchFilters);
        setAggregates(snap.selectedAggregates);
        applySidebarFromFilters(snap.searchFilters);
      }
      browseFilterSnapshotRef.current = null;
    }

    prevEntityIdRef.current = currentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrackList]);

  /*
    When the user changes sort, re-fire the appropriate fetch path
    based on current context
  */
  useEffect(() => {
    // only re-fetch when the sort actually changes
    if (!sortDidMountRef.current) {
      sortDidMountRef.current = true;
      return;
    }

    if (isSearching) {
      const active = currentSearchRef.current;
      if (active) executeSearch(active.mode, active.queryText, active.filters);
      return;
    }

    const entityType = selectedTrackList?.type;
    const entityId = selectedTrackList?.entity?.id;

    if (entityType === "Album" && entityId) {
      queryAlbumDetail({ id: entityId });
      return;
    }
    if (entityType === "Playlist" && entityId) {
      queryPlaylistDetail({ id: entityId });
      return;
    }

    fetchTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortType]);

  const wipeData = () => {
    setSuggestedTracks([]);
    setExactTracks([]);
    setIsNlFallback(false);
  };

  /*
    name - handleHoveredRange
    return type - void
    description - if user hovers over a range filer on the track,
     this function deals with displaying a hovered filter on the header.

     params       type      description

    filter         {}         a filter object that contains filterName, filterType, filterValue
  */
  const handleHoveredRange = (name, type, value, min, max) => {
    const isInFilters = searchFilters.some(item => item.filterName === name);
    if (isInFilters) return;
    setHoveredFilter({
      min: min || value,
      max: max || value,
      filterType: type,
      filterName: name,
      filterValue: value
    });
  };

  /*
    name - handleHoveredSelectable
    return type - void
    description - if user hovers over a selectable filer on the track,
      this function deals with displaying a hovered filter on the header.

    params       type          description

    name           string         name of the selectable
    type           string         type  >> >>   >>

  */
  const handleHoveredSelectable = (name, type) => {
    const isInFilters = searchFilters.some(
      item => item.filterValue === name || item.filterName === name
    );
    if (isInFilters) return;
    setHoveredFilter({
      filterType: "selectable",
      filterName: type,
      filterValue: name
    });
  };

  const clearHoveredFilter = () => {
    setHoveredFilter(undefined);
  };

  /*
    name - handleChars
    return type - void
    description - makes the sidebar characteristics array in sync with 
      applied filter given as a param.

     params       type          description

     filter        {}            a filter object
     value         []            [min, max] 
  */
  const handleChars = (filter, value) => {
    const chars = allCharacteristics.map(item => {
      if (item.filterName.toLowerCase() === filter.filterName.toLowerCase()) {
        return {
          ...item,
          min: value[0],
          max: value[1],
          filterType: "range",
          filterName: filter.filterName
        };
      }
      return item;
    });

    setChars(chars);
  };

  /*
    name - onCharChange
    return type - void
    description - this function is invoked whenever the user changes the range sliders.
      It sets the characteristics but it considers an aggregate that contains that characteristics.

     params       type          description

     filter        {}            a filter object
     value         []            [min, max] 
  */
  const onCharChange = (filter, value) => {
    setHoveredFilter(undefined);

    handleChars(filter, value);

    const containingAggregate = selectedAggregates.find(item =>
      item.filters.some(
        aggFilter =>
          aggFilter.filterName.toLocaleLowerCase() ===
          filter.filterName.toLowerCase()
      )
    );

    if (containingAggregate) {
      handleAggregateChange(containingAggregate, filter, value);
      return;
    }

    const isInFilters = searchFilters.some(
      item =>
        item.filterName.toLocaleLowerCase() ===
        filter.filterName.toLocaleLowerCase()
    );
    if (isInFilters) {
      let filters = searchFilters.map(item => {
        if (
          item.filterName.toLocaleLowerCase() ===
          filter.filterName.toLocaleLowerCase()
        )
          return {
            min: isItemLengthOrBpm(filter) ? value[0] : backendValue[value[0]],
            max: isItemLengthOrBpm(filter) ? value[1] : backendValue[value[1]],
            filterType: "range",
            filterName: filter.filterName
          };
        return item;
      });
      filters = removeDefault(filters, filter);
      setFilters(filters);
    } else {
      const isDefaultValue = filterIsDefault(filter, value);
      if (isDefaultValue) return;

      setFilters(prev => [
        ...prev,
        {
          min: isItemLengthOrBpm(filter) ? value[0] : backendValue[value[0]],
          max: isItemLengthOrBpm(filter) ? value[1] : backendValue[value[1]],
          filterType: "range",
          filterName: filter.filterName
        }
      ]);
    }
  };

  /*
  name - handleAggregateChange
  return type - void
  description - called when user applies a filter that's 
    already in an aggregate that is also applied.

  params                    type          description

  containingAggregate        {}            an aggregate object
  filter                     {}           a filter object
  value                      []            [min,max] 
  */
  const handleAggregateChange = (containingAggregate, filter, value) => {
    const filteredAggregates = selectedAggregates.filter(
      item => item.aggregateName !== containingAggregate.aggregateName
    );

    const addedFilter = {
      min: isItemLengthOrBpm(filter) ? value[0] : backendValue[value[0]],
      max: isItemLengthOrBpm(filter) ? value[1] : backendValue[value[1]],
      filterType: "range",
      filterName: filter.filterName
    };
    setAggregates(filteredAggregates);

    const decide = item => {
      if (!item.aggregate) return false;
      if (containingAggregate.aggregateName === item.aggregateName)
        return false;
      else return true;
    };

    const withAggregateFilters = searchFilters
      .map(item => ({
        ...item,
        aggregate: decide(item)
      }))
      .filter(
        item =>
          item.filterName.toLowerCase() !== addedFilter.filterName.toLowerCase()
      );

    setFilters([addedFilter, ...withAggregateFilters]);
    return;
  };

  /*
  name - handleAggregateChange
  return type - void
  description - this function handles adding a selectable and aggregate filter.
  It also handles the logic of removing clashing aggregates a from applied filters.

  params            type          description

  name              string        name of the selectable
  type              string        type of selectable (tags, instruments, keys)
  search            boolean       whether the function is called from autocomplete
  item              {}            aggregate object
*/
  const addSelectable = (name, type, search, item) => {
    setHoveredFilter(undefined);
    if (search) {
      if (type === "aggregate") {
        if (selectedAggregates.some(aggs => aggs.aggregateName === item.name))
          return;

        const aggregateTags = Object.keys(item)
          .map(key => {
            if (
              key !== "name" &&
              key !== "type" &&
              key !== "aggregateName" &&
              item[key] !== ""
            ) {
              if (key === "BPM" || key === "Length") {
                return {
                  min: item[key].min,
                  max: item[key].max,
                  filterName: key,
                  filterType: "range",
                  aggregate: true,
                  aggregateName: item.name
                };
              } else if (item[key].min || item[key].max) {
                return {
                  min: backendValue[item[key].min],
                  max: backendValue[item[key].max],
                  filterType: "range",
                  filterName: key,
                  aggregate: true,
                  aggregateName: item.name
                };
              } else if (item[key].filterType) {
                return {
                  filterType: "selectable",
                  filterName: item[key].filterName,
                  filterValue: item[key].filterValue,
                  aggregate: true,
                  aggregateName: item.name
                };
              }
              return null;
            }
            return null;
          })
          .filter(item => item !== null);
        let hash = {};
        aggregateTags.forEach(item => {
          if (item.filterType === "range")
            hash[item.filterName.toLowerCase()] = true;
          else {
            hash[item.filterValue.toLowerCase()] = true;
          }
        });

        let filtered = searchFilters.filter(item => {
          if (item.filterType === "range")
            return !hash[item.filterName.toLowerCase()];
          else {
            return !hash[item.filterValue.toLowerCase()];
          }
        });

        const aggregate = {
          aggregateName: item.name,
          filters: aggregateTags
        };

        let aggHash = {};
        aggregate.filters.forEach(item => {
          if (item.filterType === "range")
            aggHash[item.filterName.toLowerCase()] = true;
          else {
            aggHash[item.filterValue.toLowerCase()] = true;
          }
        });

        const affectedAggregates = selectedAggregates.filter(item =>
          item.filters.some(filter => {
            if (filter.filterType === "range")
              return hash[filter.filterName.toLowerCase()];
            else {
              return hash[filter.filterValue.toLowerCase()];
            }
          })
        );

        if (affectedAggregates.length === 0) {
          setAggregates([...selectedAggregates, aggregate]);
          setSidebarRanges(aggregateTags);
          setFilters([...filtered, ...aggregateTags]);

          return;
        }

        const unAffectedAggregates = selectedAggregates.filter(
          item =>
            !item.filters.some(filter => {
              if (filter.filterType === "range")
                return hash[filter.filterName.toLowerCase()];
              else {
                return hash[filter.filterValue.toLowerCase()];
              }
            })
        );

        filtered = filtered.map(filter => ({
          ...filter,
          aggregate: !affectedAggregates.some(
            item => item.aggregateName === filter.aggregateName
          )
        }));
        setAggregates([aggregate, ...unAffectedAggregates]);
        setFilters([...filtered, ...aggregateTags]);
        setSidebarRanges([...aggregateTags]);

        return;
      }

      const itemExists = searchFilters.some(item => item.filterValue === name);
      if (!itemExists) {
        if (item.type === "dateRange") {
          setFilters(prev => [
            ...prev,
            {
              filterType: item.type,
              filterName: item.name,
              filterValue: item.value,
              dateMin: item.min,
              dateMax: item.max
            }
          ]);
          return;
        }
        setFilters(prev => [
          ...prev,
          {
            filterType: "selectable",
            filterName: type,
            filterValue: name,
            hidden: false
          }
        ]);
      }

      return;
    }

    const itemExists = searchFilters.some(item => item.filterValue === name);
    if (type === "key") {
      const updated = searchFilters.filter(item => item.filterName !== "key");
      setFilters(updated);
    }
    if (itemExists) {
      const updated = searchFilters.filter(item => item.filterValue !== name);

      const containingAggregate = selectedAggregates.find(item =>
        item.filters.some(
          aggFilter =>
            aggFilter.filterType === "selectable" &&
            aggFilter.filterValue.toLocaleLowerCase() === name.toLowerCase()
        )
      );

      if (containingAggregate) {
        const filteredAggregates = selectedAggregates.filter(
          item => item.aggregateName !== containingAggregate.aggregateName
        );

        setAggregates(filteredAggregates);

        const withAggregateFilters = updated.map(item => ({
          ...item,
          aggregate: false
        }));
        setFilters(withAggregateFilters);
        return;
      }

      setFilters(updated);
    } else {
      setFilters(prev => [
        ...prev,
        {
          filterType: "selectable",
          filterName: type,
          filterValue: name
        }
      ]);
    }
  };

  /*
  name - addDateRangeFilter
  return type - void
  description - this function handles adding a date range filter.

  params            type          description

  item              Object        With the following properties:
  item.name         string        name of the filter
  item.value        string        value of filter to apply to elastic search (ex. created_at)
  item.dateMin      Date          earliest date to search
  item.dateMax      Date          latest date to search
  */
  const addDateRangeFilter = item => {
    setHoveredFilter(undefined);
    const itemExists = searchFilters.some(
      filter => filter.filterName === item.name
    );

    if (itemExists) {
      let removeFilter = searchFilters.filter(
        filter => filter.filterName !== item.name
      );

      setFilters(removeFilter);
    } else {
      setFilters(prev => [
        ...prev,
        {
          filterName: item.name,
          filterValue: item.value,
          filterType: "dateRange",
          dateMin: item.min.toString(),
          dateMax: item.max.toString()
        }
      ]);
    }
  };

  const unFavoriteTrack = trackId => {
    const newAllTracks = allTracks.map(item => {
      if (item.id === trackId) {
        item.favorite = false;

        return item;
      } else {
        return item;
      }
    });

    setAllTracks(newAllTracks);
  };

  // Remove a track from the currently-viewed playlist: deletes the
  // PlaylistTrack on the backend, then drops it from the displayed list
  // and keeps the open playlist entity (header widget) in sync.
  const removeTrackFromPlaylist = async playlistTrackId => {
    if (!playlistTrackId) return false;
    try {
      await client.mutate({
        mutation: DELETE_PLAYLIST_TRACK,
        variables: { id: playlistTrackId }
      });
      setAllTracks(prev =>
        prev.filter(track => track.playlistTrackId !== playlistTrackId)
      );
      setSelectedTrackList(prev => {
        if (!prev || prev.type !== "Playlist" || !prev.entity) return prev;
        return {
          ...prev,
          entity: {
            ...prev.entity,
            playlistTracks: (prev.entity.playlistTracks ?? []).filter(
              pt => pt.id !== playlistTrackId
            ),
            trackOrder: (prev.entity.trackOrder ?? []).filter(
              id => id !== playlistTrackId
            )
          }
        };
      });
      return true;
    } catch (err) {
      console.error("error removing track from playlist:", err);
      return false;
    }
  };

  // Persist a new manual track order for the currently-viewed playlist.
  // The displayed order is derived from entity.trackOrder (see
  // applyPlaylistTrackOrder in BrowseContainer), so we update it optimistically
  // and revert if the mutation fails. `newTrackOrder` is the full ordered list
  // of playlistTrack ids.
  const reorderPlaylistTracks = async (playlistId, newTrackOrder) => {
    if (!playlistId || !Array.isArray(newTrackOrder)) return false;
    const prevOrder = selectedTrackList?.entity?.trackOrder;
    setSelectedTrackList(prev => {
      if (!prev || prev.type !== "Playlist" || !prev.entity) return prev;
      return { ...prev, entity: { ...prev.entity, trackOrder: newTrackOrder } };
    });
    try {
      await client.mutate({
        mutation: UPDATE_PLAYLIST_TRACK_ORDER,
        variables: { id: playlistId, trackOrder: newTrackOrder }
      });
      return true;
    } catch (err) {
      console.error("error reordering playlist tracks:", err);
      setSelectedTrackList(prev => {
        if (!prev || prev.type !== "Playlist" || !prev.entity) return prev;
        return { ...prev, entity: { ...prev.entity, trackOrder: prevOrder } };
      });
      return false;
    }
  };

  /*
  name - removeFilter
  return type - void
  description - handles removing a filter from applied filters.
  There will be a case where the user can remove individual filter from an aggregate.
  When that happens, that aggregate is removed and 
  any remaining filters are applied as individual filters.

  params            type          description

  value              string        name of the filter
  type              string        type of filter
  aggregate           {}          aggregate object that the filter is being removed from

  */
  const removeFilter = (value, type, aggregate) => {
    if (textSearching) setTextSearching(false);
    setSearchValue("");

    // set sidebar
    if (["duration", "length"].includes(value.toLowerCase())) {
      handleChars({ filterName: value }, [duration.min, duration.max]);
    } else if (value.toLowerCase() === "bpm") {
      handleChars({ filterName: value }, [bpm.min, bpm.max]);
    } else {
      handleChars({ filterName: value }, [1, 5]);
    }

    if (aggregate) {
      const filteredAggregates = selectedAggregates.filter(
        item => item.aggregateName !== aggregate.aggregateName
      );
      let filtersLeft = [];

      if (type === "selectable") {
        filtersLeft = aggregate.filters
          .filter(item => {
            if (item.filterType === "range") return true;
            return item.filterValue !== value;
          })
          .map(item => ({
            ...item,
            aggregate: false
          }));
      } else {
        filtersLeft = aggregate.filters
          .filter(item => item.filterName.toLowerCase() !== value.toLowerCase())
          .map(item => ({
            ...item,
            aggregate: false
          }));
      }

      let hash = {};
      filtersLeft.forEach(item => {
        if (item.filterType === "range")
          hash[item.filterName.toLowerCase()] = true;
        else {
          hash[item.filterValue.toLowerCase()] = true;
        }
      });

      setAggregates(filteredAggregates);

      const rangeFiltersLeft = filtersLeft.filter(
        item => item.filterType === "range"
      );
      const selectableFiltersLeft = filtersLeft.filter(
        item => item.filterType === "selectable"
      );

      let updated = [];
      let rangeFilterLeftHash = {};
      rangeFiltersLeft.forEach(item => {
        rangeFilterLeftHash[item.filterName.toLowerCase()] = true;
      });

      updated = searchFilters.filter(item => {
        if (item.filterType === "selectable") return true;
        return !rangeFilterLeftHash[item.filterName.toLowerCase()];
      });

      let selectableFiltersLeftHash = {};
      selectableFiltersLeft.forEach(item => {
        selectableFiltersLeftHash[item.filterValue.toLowerCase()] = true;
      });

      updated = updated.filter(item => {
        if (item.filterType === "range") return true;
        return !selectableFiltersLeftHash[item.filterValue.toLowerCase()];
      });

      if (type === "selectable") {
        updated = updated.filter(item => {
          if (item.filterType === "range") return true;
          return value.toLowerCase() !== item.filterValue.toLowerCase();
        });

        return setFilters([...filtersLeft, ...updated]);
      }

      updated = updated.filter(item => {
        if (item.filterType === "selectable") return true;
        return value.toLowerCase() !== item.filterName.toLowerCase();
      });

      return setFilters([...filtersLeft, ...updated]);
    }

    if (type === "selectable") {
      const updated = searchFilters
        .filter(item => item.filterValue !== value)
        .map(item => ({
          ...item
        }));
      return setFilters(updated);
    }
    const updated = searchFilters
      .filter(item => item.filterName !== value)
      .map(item => ({
        ...item
      }));

    setFilters([...updated]);
  };

  /*
  name - removeAggregate
  return type - void
  description - handles removing an aggregate from applied filters.

  params            type          description

  aggregate           {}          aggregate object that is being removed 

  */
  const removeAggregate = aggregate => {
    const filteredAggregates = selectedAggregates.filter(
      item => item.aggregateName !== aggregate.aggregateName
    );
    let hash = {};
    aggregate.filters.forEach(item => {
      hash[item.filterName.toLowerCase()] = true;
    });
    const filteredSearchFilters = searchFilters.filter(
      item => !hash[item.filterName.toLowerCase()]
    );

    const sidebarFilters = generateDefaultRanges(aggregate.filters);

    //  aggregate.filters.map(item => {
    //   if (["length", "duration"].includes(item.filterName.toLowerCase())) {
    //     return {
    //       ...item,
    //       min: duration.min,
    //       max: duration.max
    //     };
    //   } else if (item.filterName.toLowerCase() === "bpm")
    //     return { ...item, min: bpm.min, max: bpm.max };

    //   return {
    //     ...item,
    //     min: 0,
    //     max: 9
    //   };
    // });

    setFilters(filteredSearchFilters);
    setSidebarRanges(sidebarFilters);
    setAggregates(filteredAggregates);
  };

  const fetchSearchResult = (query, type, item) => {
    if (!item) {
      let isAutoCompleteValue = allSearchResults.filter(x => {
        return x.name === query;
      });

      if (isAutoCompleteValue.length === 1) {
        setTextSearching(false);

        addSelectable(
          query,
          isAutoCompleteValue[0].type,
          true,
          isAutoCompleteValue[0]
        );
      } else {
        setLoadingTracks(false);
        setTextSearching(true);
        // Free-text search escapes any open entity back to a global search
        browseFilterSnapshotRef.current = null;
        setSelectedTrackList(undefined);
        // Reset ranges
        const sidebarFilters = generateDefaultRanges(searchFilters);
        setSidebarRanges(sidebarFilters);

        setFilters([
          {
            filterType: "selectable",
            filterName: "selectable",
            filterValue: query,
            hidden: false
          }
        ]);
      }
    } else {
      setTextSearching(false);

      addSelectable(query, type, true, item);
    }
  };

  /*
    Adds (or replaces) the natural-language search pill while leaving any other
    active filters in place. The main searchFilters effect detects the pill and
    runs the Cyanite search (executeSearch("NATURAL_LANGUAGE", ...)), re-running it whenever
    the other filters change so NL results stay constrained by the visible pills.
  */
  const runNaturalLanguageSearch = q => {
    const queryText = (q ?? "").trim();
    if (!queryText) return;

    // Mark the query with a dedicated filterName so the searchFilters effect
    // knows the NL results are the source of truth
    setFilters(prev => [
      ...prev.filter(item => item.filterName !== NATURAL_LANGUAGE_FILTER_NAME),
      {
        filterType: "selectable",
        filterName: NATURAL_LANGUAGE_FILTER_NAME,
        filterValue: queryText,
        hidden: false
      }
    ]);
  };

  /*
    Generic browse suggestions used as the "you might like" fallback for search
  */
  const fetchFallbackSuggestions = async (from = 0) => {
    try {
      const res = await client.query({
        query: SUGGEST_QUERY,
        fetchPolicy: "network-only",
        variables: {
          filters: [],
          from,
          randomSortMode,
          size: PAGINATION_SIZE,
          seed,
          sort: getEffectiveSort(sortType, false)
        }
      });
      return res.data.suggest.map(item => item._source);
    } catch (err) {
      console.error("Search Provider: fetchFallbackSuggestions ", err);
      return [];
    }
  };

  /*
    Runs the unified search for the given mode
  */
  const executeSearch = async (mode, queryText, filters = []) => {
    const text = (queryText ?? "").trim();

    let contextId;
    let contextType;
    const selected = selectedTrackListRef.current;
    // SIMILAR carries the seed via seedTrackId. A TRACK context would make the
    // backend hard-filter every result to the seed's own track family
    // (parentTrackId), wiping out the actual similar tracks — so never send a
    // context for SIMILAR.
    if (
      selected &&
      mode !== "SIMILAR" &&
      ["Album", "Playlist", "Track"].includes(selected.type)
    ) {
      contextId = selected.entity?.id;
      contextType = selected.type.toUpperCase();
    }

    // SIMILAR mode is seeded by the selected Track entity; the seed is NOT a filter.
    const seedTrackId = mode === "SIMILAR" ? selected?.entity?.id : undefined;

    const sort = getEffectiveSort(sortType, true);

    const searchSeed = seed;

    // remember this search so fetchMoreExactTracks can page it by offset
    currentSearchRef.current = {
      mode,
      queryText: text,
      filters,
      seedTrackId,
      contextId,
      contextType,
      sort,
      seed: searchSeed
    };

    setLoadingTracks(true);
    wipeData();
    setSuggestPaginator(false);
    setSuggestedCursor(null);

    try {
      const { data } = await client.query({
        query: SEARCH,
        fetchPolicy: "network-only",
        variables: {
          q: text,
          mode,
          seedTrackId,
          filters,
          contextId,
          contextType,
          from: 0,
          size: PAGINATION_SIZE,
          sort,
          seed: searchSeed
        }
      });
      const result = data.search || {};
      const exact = result.exact || [];
      let suggested = result.suggested || [];
      let fallback = Boolean(result.isFallback);

      // Never leave the user on an empty page.
      if (exact.length === 0 && suggested.length === 0) {
        suggested = await fetchFallbackSuggestions();
        fallback = true;
      }

      setExactTracks(exact);
      setExactTracksPaginator(result.exactHasMore ? PAGINATION_SIZE : false);
      setSuggestedTracks(suggested);

      setSuggestedCursor(
        !fallback && result.suggestedHasMore
          ? { phase: "filtered", from: PAGINATION_SIZE }
          : fallback
          ? suggested.length >= PAGINATION_SIZE
            ? { phase: "fallback", from: suggested.length }
            : null
          : { phase: "fallback", from: 0 }
      );
      setIsNlFallback(fallback);
    } catch (err) {
      console.error("Search Provider: executeSearch ", err);
      setExactTracks([]);
      setSuggestedTracks([]);
      setExactTracksPaginator(false);
      setSuggestedCursor(null);
      setIsNlFallback(false);
    } finally {
      setLoadingTracks(false);
      setLoadingSuggestions(false);
    }
  };

  /*
  name - getSuggestions
  return type - void
  description - handles text search. It searches from filters (tags,instruments, aggregates...)
   and the database for tracks, albums etc...

  params            type          description
  input             string         search term
  */
  const getSuggestions = async input => {
    if (input.trim().length === 0) return;
    client.queryManager.stopQuery();
    client.stop();
    setLoadingAutocomplete(true);

    try {
      setLoadingAutocomplete(true);
      const response = await client.query({
        query: GET_AUTOCOMPLETE,
        variables: { q: input },
        fetchPolicy: "no-cache"
      });
      const { data } = response;
      const trackData = data.autocomplete
        .map(item => {
          if (!item.variation) {
            return {
              ...item,
              type:
                item.__typename === "Album"
                  ? "session"
                  : item.__typename.toLowerCase(),
              name: item.name
            };
          } else return undefined;
        })
        .filter(item => Boolean(item));

      setAutoCompleteSuggestions([...trackData]);
      await searchFromFilters(input);
      setLoadingAutocomplete(false);
    } catch (err) {
      console.error("error:", err);
    }
  };

  /*
    description: searches from the array of tags, instruments and quick-filters
    stored on frontend.
    arguments:
      query: the search query
  */
  const searchFromFilters = query => {
    if (query.trim().length < 2) return;
    const tagResults = allTags
      .filter(tag => {
        return tag.name.toLowerCase().includes(query.toLowerCase());
      })
      .map(item => ({ ...item, type: "tags" }));

    const genreResults = allGenres
      .filter(genre => {
        return genre.name.toLowerCase().includes(query.toLowerCase());
      })
      .map(item => ({ ...item, type: "genres" }));

    const instrumentResults = allInstruments
      .filter(instrument => {
        return instrument.name.toLowerCase().includes(query.toLowerCase());
      })
      .map(item => ({ ...item, type: "instruments" }));

    const resultAggregate = aggregates
      .filter(aggregate =>
        aggregate["aggregateName"].toLowerCase().includes(query.toLowerCase())
      )
      .map(item => ({
        name: item["aggregateName"],
        type: "aggregate",
        ...item
      }));

    setAggregateResults(resultAggregate);
    setTagSearchResults(tagResults);
    setGenreSearchResults(genreResults);
    setInstrumentSearchResult(instrumentResults);
    setLoadingAutocomplete(false);
  };

  const queryAlbumDetail = async item => {
    try {
      const requestToken = {};
      activeTrackRequestRef.current = requestToken;
      // allTracks will hold this album's tracks
      allTracksContextRef.current = String(item.id);
      setLoadingTracks(true);
      setAllTracks([]);
      const { data } = await client.query({
        query: GET_ALBUM,
        variables: { id: item.id, sort: getEffectiveSort(sortType, false) }
      });

      // A newer navigation superseded this album while the query was in flight,
      // don't apply stale data over the current view
      if (activeTrackRequestRef.current !== requestToken) return;

      setLoadingTracks(false);
      setAllTracks(dedupeById(data.album.tracks));
      setSelectedTrackList({
        name: data.album.name,
        type: "Album",
        entity: data.album
      });
      setPendingWidgetEntity(null);
    } catch (err) {
      setPendingWidgetEntity(null);
      console.error("error:", err);
    }
  };

  const queryPlaylistDetail = async item => {
    try {
      const requestToken = {};
      activeTrackRequestRef.current = requestToken;
      // allTracks will hold this playlist's tracks
      allTracksContextRef.current = String(item.id);
      setLoadingTracks(true);
      setAllTracks([]);
      const { data } = await client.query({
        query: GET_PLAYLIST,
        variables: { id: item.id, sort: getEffectiveSort(sortType, false) }
      });

      // A newer navigation superseded this album while the query was in flight,
      // don't apply stale data over the current view
      if (activeTrackRequestRef.current !== requestToken) return;

      setLoadingTracks(false);
      if (data && data.playlist) {
        const tracks = (data.playlist.playlistTracks ?? []).map(pt => ({
          ...pt.tracks,
          playlistTrackId: pt.id
        }));
        setAllTracks(tracks);
        setSelectedTrackList({
          name: data.playlist.name,
          type: "Playlist",
          entity: data.playlist
        });
        setPendingWidgetEntity(null);
        return data.playlist;
      }
      return null;
    } catch (err) {
      setPendingWidgetEntity(null);
      console.error("error:", err);
      return null;
    }
  };

  const hasAutoCompleteResults = () => {
    return (
      autoCompleteSuggestions.length > 0 ||
      aggregateResults.length > 0 ||
      instrumentSearchResults.length > 0 ||
      tagSearchResults.length > 0 ||
      genreSearchResults.length > 0
    );
  };

  const clearSearchFilters = () => {
    if (searchFilters.length === 0) return;
    setFilters([]);
    setAggregates([]);
    setSidebarRanges(
      [
        ...characteristics,
        ...advancedCharacteristics,
        ...advancedCharacteristics2
      ],
      true
    );
  };

  /*
  name - setSidebarRanges
  return type - void
  description - given array of range filters (characteristics), it sets the sidebar
  to be in sync with their values.

  params            type          description

  rangeFilters      [filter]       array of filters
  useOriginal        boolean       whether to convert values or use give value 
  */
  const setSidebarRanges = (rangeFilters, useOriginal) => {
    if (!rangeFilters) return; // return is rangeFilters is null

    const filtered = rangeFilters.filter(
      // extract range filters out from rangeFilters
      item => item.filterType === "range"
    );

    const convertedToFrontend = filtered.map(item => ({
      ...item,
      min:
        isItemLengthOrBpm(item) || useOriginal
          ? item.min
          : convertToFrontend(item.min),
      max:
        isItemLengthOrBpm(item) || useOriginal
          ? item.max
          : convertToFrontend(item.max)
    }));

    const chars = allCharacteristics;

    convertedToFrontend.forEach(item => {
      allCharacteristics.forEach((filter, index) => {
        if (item.filterName.toLowerCase() === filter.filterName.toLowerCase()) {
          chars[index] = item;
        }
      });
    });

    setChars(chars);
  };

  /*
    Build the sidebar slider state
  */
  const applySidebarFromFilters = rangeFilters => {
    const base = [
      ...characteristics,
      ...advancedCharacteristics,
      ...advancedCharacteristics2
    ].map(c => ({ ...c }));

    (rangeFilters || [])
      .filter(item => item.filterType === "range")
      .forEach(item => {
        const idx = base.findIndex(
          c => c.filterName.toLowerCase() === item.filterName.toLowerCase()
        );
        if (idx === -1) return;
        // Overlay only the user's range onto the default characteristic; avoid
        // spreading item's selectable/aggregate fields onto a sidebar entry
        base[idx] = {
          ...base[idx],
          min: isItemLengthOrBpm(item) ? item.min : convertToFrontend(item.min),
          max: isItemLengthOrBpm(item) ? item.max : convertToFrontend(item.max)
        };
      });

    setChars(base);
  };

  // restores the parked browse-filter snapshot and clears the entity at once, so the
  // main effect never runs an intermediate search on leftover within-entity filters
  const exitEntityToBrowse = () => {
    const snap = browseFilterSnapshotRef.current;
    browseFilterSnapshotRef.current = null;

    setSearchValue("");
    if (snap) {
      setAggregates(snap.selectedAggregates ?? []);
      applySidebarFromFilters(snap.searchFilters ?? []);
      setFilters(snap.searchFilters ?? []);
    }
    setSelectedTrackList(undefined);
    // Clear the pending entity
    setPendingWidgetEntity(null);
    // Reload browse (also clears the entity's allTracks + context tag). With
    // non-empty restored filters the main effect runs the restored search.
    fetchTracks();
  };

  const setDownloadTrack = async track => {
    let downloadTrack = track;
    if (track.variation) {
      const all = [
        ...allTracks,
        ...suggestedTracks,
        ...exactTracks,
        singleTrack
      ];
      const parentTrack = all.find(item => item.id === track.parentTrackId);
      downloadTrack = parentTrack;

      if (!downloadTrack) {
        const fetchedTrack = await fetchTrack(track.parentTrackId);
        downloadTrack = fetchedTrack;
      }
      setTrackCache(prev => ({ ...prev, [downloadTrack.id]: downloadTrack }));
    }
    setDownloadTrackState(downloadTrack);
  };

  const fetchTrack = async id => {
    setFetchingDownloadTrack(true);
    if (tracksCache[id]) {
      setFetchingDownloadTrack(false);
      return tracksCache[id];
    }
    try {
      const { data } = await client.query({
        query: GET_TRACK,
        variables: { id: id },
        fetchPolicy: "no-cache"
      });
      setTrackCache(prev => ({ ...prev, [data.track.id]: data.track }));
      setFetchingDownloadTrack(false);
      return data.track;
    } catch (err) {
      console.error("error:", err);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        allCharacteristics,
        allInstruments,
        setLoadingAutocomplete,
        allKeys,
        allTags,
        allGenres,
        searchFilters: frontendSearchFilters,
        withinEntityFilters,
        setFilters,
        selectedAggregate,
        onCharChange,
        addDateRangeFilter,
        handleHoveredRange,
        handleHoveredSelectable,
        clearHoveredFilter,
        hoveredFilter,
        addSelectable,
        removeFilter,
        suggestions: autoCompleteSuggestions,
        setSuggestions: setAutoCompleteSuggestions,
        exactTracks,
        suggestedTracks: filteredSuggestedTracks,
        fetchSearchResult,
        runNaturalLanguageSearch,
        isNaturalLanguageSearch: searchFilters.some(
          item => item.filterName === NATURAL_LANGUAGE_FILTER_NAME
        ),
        isNlFallback,
        loadingTracks,
        searchValue,
        setSearchValue,
        allTracks,
        isSearching,
        setIsSearching,
        fetchTracks,
        getSuggestions,
        tagSearchResults,
        setTagSearchResults,
        genreSearchResults,
        setGenreSearchResults,
        instrumentSearchResults,
        setInstrumentSearchResult,
        setAllTracks,
        allTracksContextRef,
        exitEntityToBrowse,
        queryAlbumDetail,
        queryPlaylistDetail,
        trackToBeDownloaded,
        setDownloadTrack,
        aggregateResults,
        setSelectedTrackList,
        selectedTrackList,
        pendingWidgetEntity,
        setPendingWidgetEntity,
        loadingSuggestions,
        hasAutoCompleteResults,
        isLoadingAutocomplete,
        clearSearchFilters,
        unFavoriteTrack,
        removeTrackFromPlaylist,
        reorderPlaylistTracks,
        fetchMoreTracks,
        fetchMoreExactTracks,
        loadingMore,
        loadingMoreExact,
        seed,
        fetchMoreSuggested,
        hasMoreSuggested: Boolean(suggestedCursor),
        selectedAggregates,
        removeAggregate,
        setLoadingMore,
        setLoadingMoreExact,
        textSearching,
        hasMoreExactTracks: exactTracksPaginator,
        setSingleTrack,
        fetchingDownloadTrack,
        fetchTrack,
        showAllFiltersByDefault,
        toggleShowAllFiltersByDefault,
        showVariationsInSearchResults,
        toggleShowVariationsInSearchResults,
        showOnlyTracksWithStems,
        toggleShowOnlyTracksWithStems,
        sortType,
        setSortType,
        uploadedTrack,
        setUploadedTrack,
        searchMode,
        setSearchMode,
        defaultTrackView,
        filterDrawerOpen,
        openFilterDrawer,
        closeFilterDrawer
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default withRouter(SearchProvider);

export const useSearch = () => useContext(SearchContext);
